import {
  Button,
  Card,
  closeCurrentDialog,
  closeDialog,
  openDialog,
  showNotification,
  TabProps,
  TabsHeader,
  Tooltip,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {refreshDmsDatagrid, usePermissions} from '@dms/shared';
import {
  AddCreatedArticleAction,
  AddCreatedArticleDialog,
  ArticleCreate,
  getSingleOrUndefined,
} from '@dms/shared-aftersales';

import {composePath, suffixTestId, TestIdProps, useLatestValue, useNavigate} from 'shared/web';

import {DS_SPARE_PARTS_GRID_CODE} from './DS_SPARE_PARTS_GRID_CODE';
import {RequestTabMaterialCoreExchangeTab} from './RequestTabMaterialCoreExchangeTab';
import {RequestTabMaterialPriceListTab} from './RequestTabMaterialPriceListTab';
import {RequestTabMaterialWarehouseTab} from './RequestTabMaterialWarehouseTab';

interface MaterialListProps extends TestIdProps {
  onWarehouseDispatch: (warehouseArticleId: string) => Promise<void>;
  onWarehouseRequest: (warehouseArticleId: string) => Promise<boolean>;
  onWarehouseNonBinding: (warehouseArticleId: string) => Promise<boolean>;
  onCoreExchangeDispatch: (coreExchangeItemId: string) => Promise<boolean>;
  availableWarehouses: GetWarehousesResponse;
  hasPendingRequest: boolean;
}

const TABS_IDS = {
  warehouseTab: 'directSale-addMaterial-warehouseTab',
  priceListTab: 'directSale-addMaterial-priceListTab',
  coreExchangeTab: 'directSale-addMaterial-coreExchangeTab',
};

const CREATE_ARTICLE_DIALOG = 'createArticleDialog';
const ADD_CREATED_ARTICLE_DIALOG = 'addCreatedArticleDialog';

export function RequestTabMaterialList(props: MaterialListProps) {
  const navigate = useNavigate();
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [dataGridFilters, setDataGridFilters] = useState<Record<string, string>>({});

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const hasPendingRequestRef = useLatestValue(props.hasPendingRequest);

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <RequestTabMaterialWarehouseTab
          onWarehouseDispatch={props.onWarehouseDispatch}
          onWarehouseRequest={props.onWarehouseRequest}
          onWarehouseNonBinding={props.onWarehouseNonBinding}
          availableWarehouses={props.availableWarehouses}
          onDataGridFiltersChange={setDataGridFilters}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <RequestTabMaterialPriceListTab
          onWarehouseRequest={props.onWarehouseRequest}
          onWarehouseNonBinding={props.onWarehouseNonBinding}
          onWarehouseDispatch={props.onWarehouseDispatch}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.priceList', props)}
        />
      ),
    },
    {
      id: TABS_IDS.coreExchangeTab,
      title: i18n.t('entity.warehouse.labels.coreExchange'),
      content: (
        <RequestTabMaterialCoreExchangeTab
          onCoreExchangeDispatch={props.onCoreExchangeDispatch}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.coreExchange', props)}
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  const handleAddCreatedArticle = (
    warehouseArticleId: string,
    warehouseAction: AddCreatedArticleAction
  ): Promise<void> | void => {
    if (hasPendingRequestRef.current) {
      showNotification.warning(i18n.t('entity.warehouse.labels.thereIsAnotherOngoingAction'));
      return;
    }

    const addToBasket =
      warehouseAction === 'non-binding' ? props.onWarehouseNonBinding : props.onWarehouseRequest;

    // on failure the chooser stays open so the user can retry
    return addToBasket(warehouseArticleId).then((isAdded) => {
      if (!isAdded) {
        return;
      }

      closeDialog(ADD_CREATED_ARTICLE_DIALOG);
      refreshDmsDatagrid(DS_SPARE_PARTS_GRID_CODE);
    });
  };

  const openAddCreatedArticleDialog = (warehouseArticleId: string) => {
    openDialog(
      <AddCreatedArticleDialog
        onConfirm={(warehouseAction) =>
          handleAddCreatedArticle(warehouseArticleId, warehouseAction)
        }
        onClose={() => closeDialog(ADD_CREATED_ARTICLE_DIALOG)}
        data-testid={testIds.warehouse.priceList('dialogs.addCreatedArticle')}
      />,
      {
        id: ADD_CREATED_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.addCreatedArticle'),
        size: 'small',
        withAdditionalFooter: true,
      }
    );
  };

  const handleCreateArticle = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber:
            'catalogNumber' in dataGridFilters ? dataGridFilters.catalogNumber : undefined,
          manufacturerId:
            'manufacturerId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.manufacturerId)
              : undefined,
          warehouseId:
            'warehouseId' in dataGridFilters
              ? getSingleOrUndefined(dataGridFilters.warehouseId)
              : undefined,
          name: 'name' in dataGridFilters ? dataGridFilters.name : undefined,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
                isSearchParamsPreserved: false,
              })
            );
          }
          closeCurrentDialog();
          refreshDmsDatagrid(DS_SPARE_PARTS_GRID_CODE);

          if (createdArticleData.submittedButton === 'CREATE_AND_ADD') {
            openAddCreatedArticleDialog(createdArticleData.articleId);
          }
        }}
        submitButtonTitle={i18n.t('general.actions.create')}
        onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
        hasCreateAndEditButton
        hasCreateAndAddButton
        data-testid={testIds.warehouse.priceList('dialogs.createArticle')}
      />,
      {
        id: CREATE_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        withAdditionalFooter: true,
        scrollBehavior: 'outside',
      }
    );
  };

  return (
    <VStack spacing={4} height="100%">
      <HStack justify="space-between">
        <TabsHeader
          variant="condensed"
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={setActiveTabId}
          data-testid={suffixTestId('tabs', props)}
        />
        <Show when={activeTabId === TABS_IDS.warehouseTab}>
          <Tooltip
            isDisabled={articleCreate}
            label={i18n.t('module.warehouse.label.missingArticleCreatePermission')}
          >
            <Button
              variant="secondary"
              leftIcon="content/add_circle"
              onClick={handleCreateArticle}
              title={i18n.t('entity.warehouse.labels.createNewArticle')}
              isDisabled={!articleCreate}
              data-testid={suffixTestId('button.createNewArticle', props)}
            />
          </Tooltip>
        </Show>
      </HStack>
      <Card isFullHeight>
        <Box height="100%" flex={1}>
          {activeTabContent}
        </Box>
      </Card>
    </VStack>
  );
}
